import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Trash2, Plus, Edit3, MapPin, Eye, Info, X,
  Check, Bed, Bath, Users, HelpCircle,
  Camera, Compass, DollarSign
} from 'lucide-react';
import axiosInstance from '../apis/axiosConfig';


import "../pages_premium.css";
import { getFullImageUrl } from '../utils/url';
import { compressImageFile } from '../utils/imageCompressor';
export default function UpdateProperty() {
  const { id } = useParams();
  const navigate = useNavigate();
  const storedUser = localStorage.getItem('user');
  let user = { id: 'admin' };
  if (storedUser) {
    try {
      user = JSON.parse(storedUser);
    } catch (e) {
      console.error('Failed to parse stored user:', e);
    }
  }
  
  const [loading, setLoading] = useState(true);
  const [property, setProperty] = useState(null);
  const [activeTab, setActiveTab] = useState('Edit Listing');
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [seekers, setSeekers] = useState([]);
  const [fetchingSeekers, setFetchingSeekers] = useState(false);
  const [activeModal, setActiveModal] = useState(null);
  const [activeRoomTab, setActiveRoomTab] = useState('Rent');
  const [modalData, setModalData] = useState({});
  const [editingRoomIndex, setEditingRoomIndex] = useState(null);

  const fetchProperty = async () => {
    try {
      const res = await axiosInstance.get(`/properties/${id}`);
      const p = res.data.property || res.data;
      setProperty(p);
    } catch (err) {
      console.error(err);
      setError('Failed to fetch property details.');
    }
  };

  const fetchSeekers = async () => {
    try {
      setFetchingSeekers(true);
      const res = await axiosInstance.get('/flatmates');
      const data = res.data.profiles || res.data.data || (Array.isArray(res.data) ? res.data : []);
      setSeekers(data);
    } catch (err) {
      console.error("Failed to fetch seekers list:", err);
    } finally {
      setFetchingSeekers(false);
    }
  };

  const handleInitiateChat = async (seeker) => {
    const recipientId = seeker.UserId || seeker.User?.id;
    if (!recipientId) return;

    const initialMsg = prompt(`Type your initial message to request chat with ${seeker.fullName || 'seeker'}:`, `Hi ${seeker.fullName || 'there'}, I am interested in matching you with this property.`);
    if (!initialMsg || !initialMsg.trim()) return;

    try {
      const res = await axiosInstance.post('/messages/conversations/init', {
        listing_id: property.id,
        owner_id: recipientId,
        message: initialMsg.trim(),
        context_type: 'room_listing'
      });
      alert('Message request sent successfully!');
      navigate(`/messages?category=room_listing&conversationId=${res.data.conversation_id}`);
    } catch (err) {
      console.error('Failed to initiate chat:', err);
      alert(err.response?.data?.message || 'Failed to send message request.');
    }
  };

  const getMatchedSeekers = () => {
    if (!property || !seekers.length) return [];

    const propertyAddrLower = (property.address || '').toLowerCase();
    const addressParts = (property.address || '').split(',').map(s => s.trim().toLowerCase());
    const propertyCityLower = addressParts[1] || addressParts[0] || '';
    const propertyRent = parseFloat(property.rent || property.price || 0);

    return seekers
      .map(s => {
        let tags = {};
        if (s.lifestyle_tags) {
          try {
            tags = typeof s.lifestyle_tags === 'string' ? JSON.parse(s.lifestyle_tags) : s.lifestyle_tags;
          } catch (err) { console.error(err); }
        }

        // 1. Location Match
        const isLocationMatch = (tags.suburbs || []).some(suburb => {
          const subClean = suburb.split(',')[0].trim().toLowerCase();
          return propertyAddrLower.includes(subClean) || propertyCityLower.includes(subClean);
        }) || (s.address_or_area || '').toLowerCase().includes(propertyCityLower);

        // 2. Budget Match
        const maxSeekerBudget = parseFloat(s.rent_budget || 0);
        const isBudgetMatch = maxSeekerBudget > 0 ? (propertyRent <= maxSeekerBudget * 1.15) : true;

        // 3. Place Type Match
        const placeTypes = tags.placeTypes || (tags.placeType ? [tags.placeType] : []);
        const isTypeMatch = placeTypes.length > 0 ? placeTypes.some(type => {
          const typeLower = type.toLowerCase();
          if (property.offerType === 'whole') {
            return typeLower.includes('whole') || typeLower.includes('studio') || typeLower.includes('one bed') || typeLower.includes('granny');
          } else {
            return typeLower.includes('room') || typeLower.includes('share') || typeLower.includes('student');
          }
        }) : true;

        // Calculate score
        let score = 0;
        if (isLocationMatch) score += 40;
        if (isBudgetMatch) score += 40;
        if (isTypeMatch) score += 20;

        return {
          ...s,
          parsedTags: tags,
          matchScore: score
        };
      })
      .filter(s => s.matchScore >= 40 && s.UserId !== user?.id && s.User?.id !== user?.id) // only show if matches and is not current user
      .sort((a, b) => b.matchScore - a.matchScore); // Sort by highest match score first
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await fetchProperty();
      await fetchSeekers();
      setLoading(false);
    };
    load();
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50/50">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#006e80]"></div>
          <p className="text-base text-gray-500 font-bold tracking-wider">Loading Listing Manager...</p>
        </div>
      </div>
    );
  }

  if (error || !property) {
    return (
      <div className="max-w-xl mx-auto p-8 text-center bg-white rounded-3xl shadow-xl border border-gray-100 mt-20">
        <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
          <Info size={28} />
        </div>
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Error Loading Listing</h2>
        <p className="text-base text-gray-500 mb-6">{error || 'Property not found.'}</p>
        <button onClick={() => navigate(-1)} className="w-full py-3.5 bg-[#006e80] text-white rounded-xl font-bold hover:bg-[#005a69] transition-all shadow-md">
          Go Back
        </button>
      </div>
    );
  }

  // Parse JSON fields safely
  const parseJSON = (field) => {
    if (!field) return {};
    try {
      let parsed = typeof field === 'string' ? JSON.parse(field) : field;
      if (typeof parsed === 'string') {
        parsed = JSON.parse(parsed);
      }
      return parsed || {};
    } catch (e) {
      console.error("Error parsing JSON:", e);
      return {};
    }
  };

  const about_home = parseJSON(property.about_home);
  const about_room = parseJSON(property.about_room);
  const rent_and_availability = parseJSON(property.rent_and_availability);

  const addressString = property.address || '345 Queen Street, Brisbane City, Brisbane, QLD';
  const rentVal = property.rent || property.price || 234;
  const bedroomsVal = about_home.totalBedrooms || property.bedrooms || 4;
  const bathroomsVal = about_home.totalBathrooms || property.bathrooms || 3;
  const occupantsVal = about_home.totalPeople || property.occupants || 2;
  const bondVal = rent_and_availability.bond || (rentVal * 4);
  const isWhole = property.offerType === 'whole';

  // Build the list of rooms (default to at least one room based on model data if empty)
  const roomsList = about_room.rooms || [
    {
      roomName: about_room.roomName || 'Room 1',
      roomType: about_room.roomType || (isWhole ? 'Whole property' : 'Shared'),
      furnished: about_room.furnished || 'Furnished',
      bathroomAccess: about_room.bathroomAccess || 'Own',
      rent: rentVal,
      bond: bondVal,
      billsIncludedToggles: rent_and_availability.billsIncludedToggles || [],
      bedSize: about_room.bedSize || 'Single',
      roomFeatures: about_room.roomFeatures || [],
      availableDate: property.availableDate || new Date().toISOString().substring(0, 10),
      minStay: property.minStay || 'No minimum stay',
      maxStay: property.maxStay || 'No maximum stay',
      flatmatePreference: about_room.flatmatePreference || 'Anyone (inc. couples)'
    }
  ];

  // Modal Open Handlers
  const openEditDetails = () => {
    setModalData({
      address: property.address || '',
      propertyType: about_home.propertyType || 'House',
      totalBedrooms: about_home.totalBedrooms || '4',
      totalBathrooms: about_home.totalBathrooms || '3',
      totalPeople: about_home.totalPeople || '2'
    });
    setActiveModal('editDetails');
  };

  const openAboutProperty = () => {
    setModalData({
      description: about_home.description || ''
    });
    setActiveModal('aboutProperty');
  };

  const openAccepting = () => {
    setModalData({
      accepting: about_home.accepting || []
    });
    setActiveModal('accepting');
  };

  const openFeatures = () => {
    setModalData({
      parking: about_home.parking || 'No parking',
      accessibility: about_home.accessibility || {
        lift: false,
        stepFree: false,
        levelAccess: false,
        widerDoorways: false,
        rollInShower: false,
        gripRails: false
      }
    });
    setActiveModal('features');
  };

  const openWhoLivingWith = () => {
    setModalData({
      who_living_with: about_home.who_living_with || ''
    });
    setActiveModal('whoLivingWith');
  };

  // Open Room Modal for Editing
  const openRoomOverview = (index, tabName = 'Rent') => {
    setEditingRoomIndex(index);
    setActiveRoomTab(tabName);
    const room = roomsList[index];
    setModalData({
      rent: room.rent,
      bond: room.bond,
      billsIncludedToggles: room.billsIncludedToggles || [],
      roomName: room.roomName || `Room ${index + 1}`,
      roomType: room.roomType || 'Shared',
      furnished: room.furnished || 'Furnished',
      bathroomAccess: room.bathroomAccess || 'Own',
      bedSize: room.bedSize || 'Single',
      roomFeatures: room.roomFeatures || [],
      availableDate: room.availableDate || new Date().toISOString().substring(0, 10),
      minStay: room.minStay || 'No minimum stay',
      maxStay: room.maxStay || 'No maximum stay',
      flatmatePreference: room.flatmatePreference || 'Anyone (inc. couples)'
    });
    setActiveModal('roomOverview');
  };

  // Open Room Modal for Adding a New Room
  const openAddRoom = () => {
    setEditingRoomIndex(null); // null means we are adding
    setActiveRoomTab('Rent');
    const nextIndex = roomsList.length + 1;
    setModalData({
      rent: 234,
      bond: 936,
      billsIncludedToggles: [],
      roomName: `Room ${nextIndex}`,
      roomType: 'Shared',
      furnished: 'Furnished',
      bathroomAccess: 'Own',
      bedSize: 'Single',
      roomFeatures: [],
      availableDate: new Date().toISOString().substring(0, 10),
      minStay: 'No minimum stay',
      maxStay: 'No maximum stay',
      flatmatePreference: 'Anyone (inc. couples)'
    });
    setActiveModal('roomOverview');
  };

  // Save changes to backend
  const handleSaveModal = async (updatedFields) => {
    try {
      setSaving(true);
      await axiosInstance.put(`/properties/${id}`, updatedFields);
      await fetchProperty();
      setActiveModal(null);
    } catch (err) {
      console.error("Failed to update listing:", err);
      alert("Failed to save changes. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // Save Room (either edit existing or append new)
  const handleSaveRoom = async () => {
    let updatedRooms = [...roomsList];
    const newRoomData = {
      roomName: modalData.roomName,
      roomType: modalData.roomType,
      furnished: modalData.furnished,
      bathroomAccess: modalData.bathroomAccess,
      rent: parseFloat(modalData.rent),
      bond: parseFloat(modalData.bond),
      billsIncludedToggles: modalData.billsIncludedToggles,
      bedSize: modalData.bedSize,
      roomFeatures: modalData.roomFeatures,
      availableDate: modalData.availableDate,
      minStay: modalData.minStay,
      maxStay: modalData.maxStay,
      flatmatePreference: modalData.flatmatePreference
    };

    if (editingRoomIndex !== null) {
      updatedRooms[editingRoomIndex] = newRoomData;
    } else {
      updatedRooms.push(newRoomData);
    }

    const updatedAboutRoom = {
      ...about_room,
      rooms: updatedRooms,
      roomName: updatedRooms[0].roomName,
      roomType: updatedRooms[0].roomType,
      furnished: updatedRooms[0].furnished,
      bathroomAccess: updatedRooms[0].bathroomAccess,
      bedSize: updatedRooms[0].bedSize,
      roomFeatures: updatedRooms[0].roomFeatures,
      flatmatePreference: updatedRooms[0].flatmatePreference
    };

    const updatedRentAndAvailability = {
      ...rent_and_availability,
      bond: updatedRooms[0].bond,
      billsIncludedToggles: updatedRooms[0].billsIncludedToggles
    };

    const payload = {
      rent: parseFloat(updatedRooms[0].rent),
      availableDate: updatedRooms[0].availableDate,
      minStay: updatedRooms[0].minStay,
      maxStay: updatedRooms[0].maxStay,
      about_room: JSON.stringify(updatedAboutRoom),
      rent_and_availability: JSON.stringify(updatedRentAndAvailability)
    };

    await handleSaveModal(payload);
  };

  // Format Image URL
  const getImageUrl = () => {
    if (property.PropertyImages && property.PropertyImages.length > 0) {
      const url = property.PropertyImages[0].image_url || property.PropertyImages[0];
      return getFullImageUrl(url);
    }
    return 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=1000&q=80';
  };

  // Upload photos to database (Max 3 photos, Max 10MB per photo, Ultra-HD optimized)
  const handlePhotoUpload = async (e) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const selectedFiles = Array.from(e.target.files);
    e.target.value = '';

    const MAX_PHOTOS = 3;
    const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
    const existingCount = property.PropertyImages ? property.PropertyImages.length : 0;
    const remainingSlots = Math.max(0, MAX_PHOTOS - existingCount);

    const oversized = selectedFiles.filter(f => f.size > MAX_FILE_SIZE_BYTES);
    const validFiles = selectedFiles.filter(f => f.size <= MAX_FILE_SIZE_BYTES);

    if (oversized.length > 0) {
      alert('Each photo must be under 10 MB!');
      if (validFiles.length === 0) return;
    }

    if (existingCount >= MAX_PHOTOS || validFiles.length > remainingSlots) {
      alert('Only up to 3 photos are allowed per listing!');
      if (remainingSlots === 0) return;
    }

    const filesToUpload = validFiles.slice(0, remainingSlots);
    if (filesToUpload.length === 0) return;

    try {
      setSaving(true);
      const compressedResults = await Promise.all(filesToUpload.map(f => compressImageFile(f)));
      const formData = new FormData();
      compressedResults.forEach(({ file }) => {
        formData.append('images', file);
      });
      await axiosInstance.put(`/properties/${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      await fetchProperty();
    } catch (err) {
      console.error("Failed to upload photos:", err);
      alert(err.response?.data?.message || "Failed to upload photos. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // Delete image from database
  const handleDeleteImage = async (imageId) => {
    if (!window.confirm("Are you sure you want to delete this photo?")) return;
    try {
      setSaving(true);
      await axiosInstance.delete(`/properties/${id}/images/${imageId}`);
      await fetchProperty();
    } catch (err) {
      console.error("Failed to delete photo:", err);
      alert("Failed to delete photo. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // Accepting tags options
  const ACCEPTING_TAGS = [
    'Pets', 'LGBTQIA+', 'Students', 'Children', 'Smokers', 
    'Women only flat', '40+ years', 'Backpackers', 'Retirees', 'Unemployed/Welfare'
  ];

  // Room features lists
  const ROOM_FEATURES = [
    { id: 'bedSideTable', label: 'Bed side table' },
    { id: 'wardrobe', label: 'Wardrobe' },
    { id: 'drawers', label: 'Drawers' },
    { id: 'ac', label: 'Air conditioner' },
    { id: 'heater', label: 'Heater' },
    { id: 'desk', label: 'Desk' },
    { id: 'lamp', label: 'Lamp' },
    { id: 'chair', label: 'Chair' },
    { id: 'couch', label: 'Couch' },
    { id: 'tv', label: 'TV' },
    { id: 'balcony', label: 'Balcony' },
    { id: 'doorLock', label: 'Door lock' },
    { id: 'kitchenette', label: 'Kitchenette' }
  ];

  // Helper for sequential wizard step navigation inside Room modal
  const handleRoomNextStep = (currentTab) => {
    const steps = ['Rent', 'Details', 'Features', 'Availability', 'Flatmate preference'];
    const currIndex = steps.indexOf(currentTab);
    if (currIndex !== -1 && currIndex < steps.length - 1) {
      setActiveRoomTab(steps[currIndex + 1]);
    }
  };

  return (
    <div className="page-premium-wrapper"><div className="page-premium-content p-0" style={{backgroundColor: "#f9fafb", minHeight: "100vh"}}><div className="min-h-screen bg-[#f4f6f8] text-gray-800 font-sans pb-16 relative">
      
      {/* Top Banner Address / Info Bar */}
      <div className="bg-white border-b border-gray-200/80 py-3 px-6 md:px-12 flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
        <div className="flex items-center gap-3.5">
          <div className="w-10 h-10 rounded-full bg-[#006e80]/10 flex items-center justify-center text-[#006e80] shrink-0">
            <MapPin size={20} />
          </div>
          <h2 className="text-base md:text-lg font-black text-[#1a1b24] tracking-tight">
            {addressString}
          </h2>
        </div>
        <div className="flex items-center gap-3">
          <button 
            onClick={() => window.open(`/rooms/${id}`, '_blank')}
            className="text-sm text-white bg-[#006e80] hover:bg-[#005a69] font-extrabold transition-all px-4 py-2 rounded-xl flex items-center gap-1.5 shadow-md shadow-[#006e80]/10 hover:-translate-y-0.5"
          >
            <Eye size={16} /> View Live
          </button>
          <button onClick={() => navigate(-1)} className="text-sm text-gray-600 hover:text-gray-900 font-extrabold transition-colors bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-xl">
            Back to Dashboard
          </button>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="px-6 md:px-12 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-5xl mx-auto flex gap-12">
          {['Edit Listing', 'Matches and Listing Views'].map(tab => {
            const active = activeTab === tab;
            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-3 text-sm md:text-base font-black transition-all relative ${
                  active ? 'text-[#006e80]' : 'text-gray-400 hover:text-gray-800'
                }`}
              >
                {tab}
                {active && (
                  <div className="absolute bottom-0 left-0 right-0 h-[4px] bg-[#006e80] rounded-t-full" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-5 space-y-5 animate-fadeIn">
        {activeTab === 'Edit Listing' && (
          <>
            {/* Modern Media / Photo Gallery Section */}
        <div className="bg-white rounded-2xl p-4 md:p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-300 space-y-4">
          <div className="flex flex-col md:flex-row items-center gap-4">
            
            {/* Add Photos Area */}
            <div role="button" tabIndex={0} onKeyDown={(e) => { if(e.key === "Enter") document.getElementById("photo-upload-input").click(); }} onClick={() => document.getElementById("photo-upload-input").click()}
              className="flex flex-col items-center justify-center bg-gray-50 hover:bg-gray-100/50 rounded-2xl p-4 w-full md:w-52 h-44 border-2 border-dashed border-gray-200 transition-colors group cursor-pointer relative"
            >
              <input 
                type="file" 
                multiple 
                accept="image/*" 
                id="photo-upload-input" 
                className="hidden" 
                onChange={handlePhotoUpload} 
              />
              <div className="w-10 h-10 rounded-full bg-[#006e80]/10 flex items-center justify-center text-[#006e80] mb-2 group-hover:scale-110 transition-transform">
                <Camera size={20} />
              </div>
              <span className="text-xs text-gray-500 font-extrabold uppercase tracking-wider mb-2">
                {property.PropertyImages ? property.PropertyImages.length : 0} / 3 Photos (Max 10 MB)
              </span>
              <button className="bg-[#1e2538] hover:bg-[#2c354e] text-white text-xs font-extrabold px-4 py-2 rounded-xl transition-colors shadow-sm">
                Add photos
              </button>
            </div>
            
            {/* Main Cover Image Area */}
            <div className="flex-1 w-full relative rounded-2xl overflow-hidden h-44 bg-gray-100 shadow-inner group">
              <img src={getImageUrl()} alt="Property Cover" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-65"></div>
              
              <span className="absolute top-3 left-3 bg-[#006e80] text-white text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full shadow-md">
                Cover Photo
              </span>
              
              {property.PropertyImages && property.PropertyImages.length > 0 && (
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteImage(property.PropertyImages[0].id);
                  }}
                  className="absolute bottom-3 left-3 bg-white/95 hover:bg-red-50 text-gray-700 hover:text-red-600 p-2 rounded-xl transition-all shadow-lg hover:scale-105 backdrop-blur-sm border border-white/50 z-10"
                >
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          </div>

          {/* List of all photo thumbnails with delete icons */}
          {property.PropertyImages && property.PropertyImages.length > 0 && (
            <div className="border-t border-gray-100 pt-3">
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">All Photos</p>
              <div className="flex flex-wrap gap-2.5">
                {property.PropertyImages.map((img, idx) => {
                  const url = getFullImageUrl(img.image_url);
                  return (
                    <div key={img.id} className="w-16 h-12 rounded-xl border border-gray-200 overflow-hidden relative group shadow-sm bg-slate-50 shrink-0">
                      <img src={url} className="w-full h-full object-cover" alt={`Property thumbnail ${idx + 1}`} />
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteImage(img.id);
                        }}
                        className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-600 hover:bg-red-700 text-white flex items-center justify-center shadow-md hover:scale-105 transition-all opacity-0 group-hover:opacity-100"
                        title="Delete photo"
                      >
                        <X size={10} />
                      </button>
                      {idx === 0 && (
                        <div className="absolute bottom-0 left-0 right-0 bg-[#006e80] text-white text-[8px] font-black uppercase tracking-wider text-center py-0.5 leading-none">
                          Cover
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Premium Details Card */}
        <div className="bg-white rounded-2xl p-5 md:p-6 shadow-sm border border-gray-100/80 space-y-5">
          
          {/* Title & Edit Section */}
          <div className="flex justify-between items-start gap-4">
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-2xl md:text-3xl font-black text-gray-900 tracking-tight leading-none">
                  {property.address || 'Brisbane City, Brisbane'}
                </h1>
                <button 
                  onClick={openEditDetails}
                  className="inline-flex items-center gap-1.5 text-[#006e80] hover:text-[#005a69] text-xs font-black bg-[#006e80]/10 hover:bg-[#006e80]/20 px-3.5 py-1.5 rounded-full transition-all"
                >
                  <Edit3 size={13} /> Edit Details
                </button>
              </div>
              
              {/* Custom Badges */}
              <div className="flex items-center gap-2.5 mt-3">
                <span className="text-[11px] font-black border-2 border-[#006e80]/20 bg-[#006e80]/5 text-[#006e80] rounded-full px-3.5 py-1 uppercase tracking-wider">
                  Early Bird
                </span>
                <span className="text-xs md:text-sm text-gray-400 font-extrabold uppercase tracking-wider">
                  {isWhole ? 'Whole property for rent' : 'Shared room for rent'}
                </span>
              </div>
            </div>
          </div>

          <hr className="border-gray-100" />

          {/* Quick Specifications Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3.5">
            
            {/* Rent Box */}
            <div className="bg-gradient-to-br from-[#006e80] to-[#00899c] rounded-xl px-4 py-3 text-white flex flex-col justify-center min-w-[130px] shadow-md shadow-[#006e80]/10 relative overflow-hidden group">
              <div className="absolute -right-4 -bottom-4 w-16 h-16 bg-white/10 rounded-full blur-xl group-hover:scale-150 transition-transform duration-500"></div>
              <span className="text-xl md:text-2xl font-black leading-none">${rentVal}<span className="text-xs font-bold opacity-85">/wk</span></span>
              <span className="text-[10px] font-black uppercase tracking-wider opacity-85 mt-1">Inc. bills</span>
            </div>
            
            {/* Bedrooms Specs Box */}
            <div className="bg-gray-50/80 hover:bg-gray-50 border border-gray-100 rounded-xl px-4 py-3 flex items-center gap-3 transition-colors">
              <div className="bg-[#006e80]/10 text-[#006e80] p-2.5 rounded-lg"><Bed size={20} /></div>
              <div>
                <p className="text-lg md:text-xl font-black text-gray-900 leading-none">{bedroomsVal}</p>
                <p className="text-[10px] md:text-xs font-extrabold text-gray-400 uppercase tracking-wider mt-1">Bedrooms</p>
              </div>
            </div>

            {/* Bathrooms Specs Box */}
            <div className="bg-gray-50/80 hover:bg-gray-50 border border-gray-100 rounded-xl px-4 py-3 flex items-center gap-3 transition-colors">
              <div className="bg-[#006e80]/10 text-[#006e80] p-2.5 rounded-lg"><Bath size={20} /></div>
              <div>
                <p className="text-lg md:text-xl font-black text-gray-900 leading-none">{bathroomsVal}</p>
                <p className="text-[10px] md:text-xs font-extrabold text-gray-400 uppercase tracking-wider mt-1">Bathrooms</p>
              </div>
            </div>

            {/* Occupants Specs Box */}
            <div className="bg-gray-50/80 hover:bg-gray-50 border border-gray-100 rounded-xl px-4 py-3 flex items-center gap-3 transition-colors">
              <div className="bg-[#006e80]/10 text-[#006e80] p-2.5 rounded-lg"><Users size={20} /></div>
              <div>
                <p className="text-lg md:text-xl font-black text-gray-900 leading-none">{occupantsVal}</p>
                <p className="text-[10px] md:text-xs font-extrabold text-gray-400 uppercase tracking-wider mt-1">Occupants</p>
              </div>
            </div>
          </div>

          <hr className="border-gray-100" />

          {/* Premium Info Panels Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            
            {/* About Property */}
            <div className="bg-white border border-gray-100 rounded-xl p-4 space-y-2.5 hover:shadow-md hover:border-gray-200/60 transition-all">
              <div className="flex justify-between items-center">
                <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest">About the property</h3>
                <button 
                  onClick={openAboutProperty}
                  className="text-xs text-[#006e80] hover:text-[#005a69] font-extrabold flex items-center gap-1 bg-[#006e80]/5 px-2.5 py-1 rounded-lg"
                >
                  {about_home.description ? <Edit3 size={12} /> : <Plus size={14} />} {about_home.description ? 'Edit' : 'Add Info'}
                </button>
              </div>
              {about_home.description ? (
                <div className="text-xs md:text-sm text-gray-700 bg-gray-50 p-3 rounded-xl border border-gray-100 leading-relaxed font-semibold max-h-40 overflow-y-auto pr-2 scrollbar-thin">
                  {about_home.description}
                </div>
              ) : (
                <div className="bg-[#eaf4fe] border border-blue-100/50 rounded-xl p-3 flex items-start gap-2.5">
                  <Info size={16} className="text-blue-500 shrink-0 mt-0.5" />
                  <p className="text-xs text-blue-700 font-extrabold leading-relaxed">Add specific details to help your place stand out to tenants.</p>
                </div>
              )}
            </div>

            {/* Property Accepting Of */}
            <div className="bg-white border border-gray-100 rounded-xl p-4 space-y-2.5 hover:shadow-md hover:border-gray-200/60 transition-all">
              <div className="flex justify-between items-center">
                <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest">Property accepting of</h3>
                <button 
                  onClick={openAccepting}
                  className="text-xs text-[#006e80] hover:text-[#005a69] font-extrabold flex items-center gap-1 bg-[#006e80]/5 px-2.5 py-1 rounded-lg"
                >
                  {about_home.accepting && about_home.accepting.length > 0 ? <Edit3 size={12} /> : <Plus size={14} />} {about_home.accepting && about_home.accepting.length > 0 ? 'Edit' : 'Add Criteria'}
                </button>
              </div>
              {about_home.accepting && about_home.accepting.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {about_home.accepting.map(tag => (
                    <span key={tag} className="bg-gray-100 text-gray-700 border border-gray-200 font-black text-xs px-3 py-1 rounded-lg shadow-sm">
                      {tag}
                    </span>
                  ))}
                </div>
              ) : (
                <div className="bg-[#eaf4fe] border border-blue-100/50 rounded-xl p-3 flex items-start gap-2.5">
                  <Info size={16} className="text-blue-500 shrink-0 mt-0.5" />
                  <p className="text-xs text-blue-700 font-extrabold leading-relaxed">Tell people who you would like to live with or have in the house.</p>
                </div>
              )}
            </div>

            {/* Property Features */}
            <div className="bg-white border border-gray-100 rounded-xl p-4 space-y-2.5 hover:shadow-md hover:border-gray-200/60 transition-all">
              <div className="flex justify-between items-center">
                <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest">Property features</h3>
                <button 
                  onClick={openFeatures}
                  className="text-xs text-[#006e80] hover:text-[#005a69] font-extrabold flex items-center gap-1 bg-[#006e80]/5 px-2.5 py-1 rounded-lg"
                >
                  {(about_home.parking && about_home.parking !== 'No parking') || (about_home.accessibility && Object.values(about_home.accessibility).some(Boolean)) ? <Edit3 size={12} /> : <Plus size={14} />} 
                  {(about_home.parking && about_home.parking !== 'No parking') || (about_home.accessibility && Object.values(about_home.accessibility).some(Boolean)) ? 'Edit' : 'Add Features'}
                </button>
              </div>
              {(about_home.parking || (about_home.accessibility && Object.values(about_home.accessibility).some(Boolean))) ? (
                <div className="text-xs font-bold text-gray-700 bg-gray-50/50 p-3 rounded-xl border border-gray-100 space-y-1.5">
                  <p className="font-extrabold text-gray-900">Parking: <span className="font-medium text-gray-600">{about_home.parking || 'No parking'}</span></p>
                  {about_home.accessibility && Object.values(about_home.accessibility).some(Boolean) && (
                    <div>
                      <p className="font-extrabold text-gray-900 mb-1">Accessibility features:</p>
                      <div className="flex flex-wrap gap-1.5">
                        {Object.entries(about_home.accessibility)
                          .filter(([, active]) => active)
                          .map(([key]) => (
                            <span key={key} className="bg-blue-50 text-blue-700 border border-blue-100 text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded">
                              {key.replace(/([A-Z])/g, ' $1')}
                            </span>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-[#eaf4fe] border border-blue-100/50 rounded-xl p-3 flex items-start gap-2.5">
                  <Info size={16} className="text-blue-500 shrink-0 mt-0.5" />
                  <p className="text-xs text-blue-700 font-extrabold leading-relaxed">Add home accessibility features, smart systems, or key amenities.</p>
                </div>
              )}
            </div>

            {/* Who you'll be living with */}
            <div className="bg-white border border-gray-100 rounded-xl p-4 space-y-2.5 hover:shadow-md hover:border-gray-200/60 transition-all">
              <div className="flex justify-between items-center">
                <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest">Who you&apos;ll be living with</h3>
                <button 
                  onClick={openWhoLivingWith}
                  className="text-xs text-[#006e80] hover:text-[#005a69] font-extrabold flex items-center gap-1 bg-[#006e80]/5 px-2.5 py-1 rounded-lg"
                >
                  {about_home.who_living_with ? <Edit3 size={12} /> : <Plus size={14} />} {about_home.who_living_with ? 'Edit' : 'Add Profile'}
                </button>
              </div>
              {about_home.who_living_with ? (
                <div className="text-xs md:text-sm text-gray-700 bg-gray-50 p-3 rounded-xl border border-gray-100 leading-relaxed font-semibold max-h-40 overflow-y-auto pr-2 scrollbar-thin">
                  {about_home.who_living_with}
                </div>
              ) : (
                <div className="bg-[#eaf4fe] border border-blue-100/50 rounded-xl p-3 flex items-start gap-2.5">
                  <Info size={16} className="text-blue-500 shrink-0 mt-0.5" />
                  <p className="text-xs text-blue-700 font-extrabold leading-relaxed">Describe the current housemates or occupants of the property.</p>
                </div>
              )}
            </div>

          </div>

          <hr className="border-gray-100" />

          {/* Room Overview Section (Can manage multiple rooms) */}
          <div className="space-y-4 pt-1">
            <div className="flex justify-between items-center">
              <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest">Room overview</h3>
            </div>
            
            {/* List all active rooms */}
            <div className="space-y-4">
              {roomsList.map((room, index) => (
                <div key={index} className="bg-gray-50/50 p-4 md:p-5 rounded-2xl border border-gray-200/60 shadow-sm relative group space-y-3">
                  <div className="flex justify-between items-center">
                    <h4 className="text-sm md:text-base font-extrabold text-[#006e80] tracking-tight">{room.roomName || `Room ${index + 1}`}</h4>
                    <button 
                      onClick={() => openRoomOverview(index, 'Rent')}
                      className="text-xs text-[#006e80] hover:text-[#005a69] font-black flex items-center gap-1 bg-[#006e80]/10 px-2.5 py-1 rounded-lg transition-colors"
                    >
                      <Edit3 size={12} /> Edit {room.roomName}
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs md:text-sm font-extrabold text-gray-700">
                    <div role="button" tabIndex={0} onKeyDown={(e) => { if(e.key === "Enter") openRoomOverview(index, "Rent"); }} className="flex items-center gap-3 bg-white p-3 rounded-xl shadow-sm cursor-pointer" onClick={() => openRoomOverview(index, "Rent")}>
                      <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center font-black text-sm">$</div>
                      <div>
                        <p className="text-gray-950 font-black text-xs md:text-sm">${room.rent} weekly rent</p>
                        <p className="text-[10px] text-gray-400 font-bold mt-0.5">${room.bond} bond</p>
                      </div>
                    </div>
                    <div role="button" tabIndex={0} onKeyDown={(e) => { if(e.key === "Enter") openRoomOverview(index, "Rent"); }} className="flex items-center gap-3 bg-white p-3 rounded-xl shadow-sm cursor-pointer" onClick={() => openRoomOverview(index, "Rent")}>
                      <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-500 flex items-center justify-center"><HelpCircle size={16} /></div>
                      <div>
                        <p className="text-gray-950 font-black text-xs md:text-sm">Bills included in rent</p>
                        <p className="text-[10px] text-gray-400 font-bold mt-0.5">
                          {room.billsIncludedToggles && room.billsIncludedToggles.length > 0 
                            ? room.billsIncludedToggles.join(', ') 
                            : 'None'}
                        </p>
                      </div>
                    </div>
                    <div role="button" tabIndex={0} onKeyDown={(e) => { if(e.key === "Enter") openRoomOverview(index, "Details"); }} className="flex items-center gap-3 bg-white p-3 rounded-xl shadow-sm cursor-pointer" onClick={() => openRoomOverview(index, "Details")}>
                      <div className="w-8 h-8 rounded-lg bg-purple-50 text-purple-500 flex items-center justify-center"><Bed size={16} /></div>
                      <div>
                        <p className="text-gray-950 font-black text-xs md:text-sm">{room.roomType || 'Shared'} room</p>
                        <p className="text-[10px] text-gray-400 font-bold mt-0.5">Furnished: {room.furnished || 'Furnished'}</p>
                      </div>
                    </div>
                    <div role="button" tabIndex={0} onKeyDown={(e) => { if(e.key === "Enter") openRoomOverview(index, "Details"); }} className="flex items-center gap-3 bg-white p-3 rounded-xl shadow-sm cursor-pointer" onClick={() => openRoomOverview(index, "Details")}>
                      <div className="w-8 h-8 rounded-lg bg-pink-50 text-pink-500 flex items-center justify-center"><Bath size={16} /></div>
                      <div>
                        <p className="text-gray-950 font-black text-xs md:text-sm">{room.bathroomAccess || 'Own'} bathroom</p>
                        <p className="text-[10px] text-gray-400 font-bold mt-0.5">Bathroom Access</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <button 
              onClick={openAddRoom}
              className="text-xs bg-[#006e80]/5 hover:bg-[#006e80]/10 text-[#006e80] font-black py-2.5 px-6 rounded-xl transition-all flex items-center justify-center gap-2 border border-[#006e80]/15 shadow-sm"
            >
              <Plus size={16} /> Add Room
            </button>
          </div>

          <hr className="border-gray-100" />

          {/* Property Location Map Section */}
          <div className="space-y-3">
            <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest flex items-center gap-1.5">
              <Compass size={16} className="text-[#006e80]" /> Property Location
            </h3>
            <div className="h-64 bg-gray-100 rounded-2xl relative overflow-hidden border border-gray-200 shadow-inner">
              <iframe
                title="Location Map"
                src={`https://maps.google.com/maps?q=${encodeURIComponent(property.address || 'Brisbane City')}&output=embed`}
                className="w-full h-full border-0"
                loading="lazy"
              />
            </div>
          </div>

        </div>
        </>)}

        {activeTab === 'Matches and Listing Views' && (
          <div className="space-y-6 animate-fadeIn">
            <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100 space-y-2">
              <h2 className="text-xl font-black text-gray-900 flex items-center gap-2">
                <Users className="text-[#006e80]" size={22} /> Matched Flatmate Seekers
              </h2>
              <p className="text-xs text-gray-500 font-bold leading-relaxed">
                We analyzed all active flatmate seeker profiles in the system to recommend matching roommates. We match seekers based on preferred locations (suburbs), weekly rent budget, and preferred accommodation types.
              </p>
            </div>

            {fetchingSeekers ? (
              <div className="bg-white rounded-3xl p-12 text-center border border-gray-100 shadow-sm">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#006e80] mx-auto mb-3"></div>
                <p className="text-xs text-gray-500 font-bold">Scanning seeker profiles for matches...</p>
              </div>
            ) : getMatchedSeekers().length === 0 ? (
              <div className="bg-white rounded-3xl p-12 text-center border border-gray-100 shadow-sm space-y-4">
                <div className="w-16 h-16 bg-gray-50 text-gray-400 rounded-full flex items-center justify-center mx-auto">
                  <Users size={28} />
                </div>
                <div>
                  <h3 className="text-base font-black text-gray-900">No matches found yet</h3>
                  <p className="text-xs text-gray-500 font-semibold mt-1">We couldn&apos;t find active seekers matching this property&apos;s location and rent price. We will match them as soon as new seeker profiles are listed.</p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {getMatchedSeekers().map((seeker) => {
                  const rawPic = seeker.profilePicture || seeker.User?.StudentProfile?.profile_picture || seeker.User?.AgentProfile?.profile_picture;
                  const picSrc = rawPic 
                    ? getFullImageUrl(rawPic)
                    : 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&q=80&w=800';

                  const scoreColor = seeker.matchScore >= 80 
                    ? 'bg-green-50 text-green-700 border-green-200' 
                    : seeker.matchScore >= 60 
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-250' 
                      : 'bg-amber-50 text-amber-700 border-amber-200';

                  return (
                    <div key={seeker.id} className="bg-white rounded-3xl border border-gray-100 hover:shadow-md transition-all duration-300 p-5 flex flex-col justify-between relative overflow-hidden group">
                      
                      {/* Match Score Badge */}
                      <span className={`absolute top-4 right-4 text-[10px] font-black px-2.5 py-1 rounded-full border tracking-wide uppercase ${scoreColor}`}>
                        {seeker.matchScore}% Match
                      </span>

                      <div className="flex gap-4">
                        {/* Seeker image */}
                        <div className="w-16 h-16 rounded-2xl overflow-hidden shrink-0 border border-gray-100 shadow-inner">
                          <img src={picSrc} className="w-full h-full object-cover" alt={seeker.fullName} />
                        </div>

                        {/* Seeker info */}
                        <div className="space-y-1">
                          <h4 className="font-black text-gray-900 text-base">{seeker.fullName}</h4>
                          <p className="text-xs text-gray-500 font-semibold">
                            {seeker.parsedTags.age ? `${seeker.parsedTags.age} year old ` : ''}{seeker.parsedTags.gender || 'Person'}
                          </p>
                          <div className="flex items-center gap-1 text-[11px] text-gray-400 font-bold">
                            <DollarSign size={12} className="text-[#006e80]" />
                            <span>Budget: ${seeker.rent_budget}/wk</span>
                          </div>
                        </div>
                      </div>

                      {/* Pref Suburbs & Tags */}
                      <div className="mt-4 pt-3 border-t border-gray-50 space-y-2">
                        <div className="flex flex-wrap gap-1.5">
                          {(seeker.parsedTags.suburbs || []).slice(0, 3).map((sub) => (
                            <span key={sub} className="text-[10px] bg-gray-50 text-gray-600 font-bold px-2 py-1 rounded-lg border border-gray-100 flex items-center gap-1">
                              <MapPin size={9} /> {sub.split(',')[0]}
                            </span>
                          ))}
                          {(seeker.parsedTags.placeTypes || (seeker.parsedTags.placeType ? [seeker.parsedTags.placeType] : [])).slice(0, 2).map((type) => (
                            <span key={type} className="text-[10px] bg-green-50/50 text-green-700 font-bold px-2 py-1 rounded-lg border border-green-100">
                              {type}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Card buttons */}
                      <div className="flex gap-3 mt-5">
                        <button
                          type="button"
                          onClick={() => window.open(`/flatmates/${seeker.id}`, '_blank')}
                          className="flex-1 py-2 text-center text-xs font-black rounded-xl text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200 transition-colors"
                        >
                          View Profile
                        </button>
                        <button
                          type="button"
                          onClick={() => handleInitiateChat(seeker)}
                          className="flex-1 py-2 text-center text-xs font-black rounded-xl text-white bg-[#006e80] hover:bg-[#005a69] shadow-sm transition-colors"
                        >
                          Send Message
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

      </div>

      {/* ========================================== */}
      {/* MODALS SECTION                             */}
      {/* ========================================== */}
      {activeModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          
          {/* Modal Card */}
          <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 w-full max-w-xl p-6 md:p-10 relative my-8">
            
            {/* Close Button */}
            <button 
              onClick={() => setActiveModal(null)}
              className="absolute top-5 right-5 text-gray-400 hover:text-gray-600 transition-colors p-2 bg-gray-50 hover:bg-gray-100 rounded-full"
            >
              <X size={20} />
            </button>

            {/* 1. Edit Details Modal */}
            {activeModal === 'editDetails' && (
              <div className="space-y-6">
                <h3 className="text-xl md:text-2xl font-black text-center text-gray-900">Edit details</h3>
                
                <div className="space-y-4">
                  <div>
                    <input 
                      type="text" 
                      value={modalData.address} 
                      onChange={(e) => setModalData({ ...modalData, address: e.target.value })}
                      placeholder="Property address"
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500 font-bold"
                    />
                    <p className="text-xs text-gray-400 mt-2 font-bold cursor-pointer hover:underline">Having trouble finding your address?</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Type of Property */}
                    <div>
                      <select 
                        value={modalData.propertyType} 
                        onChange={(e) => setModalData({ ...modalData, propertyType: e.target.value })}
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 font-bold appearance-none"
                        style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22currentColor%22 stroke-width=%222%22 stroke-linecap=%22round%22 stroke-linejoin=%22round%22%3E%3Cpolyline points=%226 9 12 15 18 9%22/%3E%3C/svg%3E")', backgroundPosition: 'right 1rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1em' }}
                      >
                        <option value="House">House</option>
                        <option value="Flat">Flat</option>
                      </select>
                    </div>

                    {/* Bedrooms */}
                    <div>
                      <select 
                        value={modalData.totalBedrooms} 
                        onChange={(e) => setModalData({ ...modalData, totalBedrooms: e.target.value })}
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 font-bold appearance-none"
                        style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22currentColor%22 stroke-width=%222%22 stroke-linecap=%22round%22 stroke-linejoin=%22round%22%3E%3Cpolyline points=%226 9 12 15 18 9%22/%3E%3C/svg%3E")', backgroundPosition: 'right 1rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1em' }}
                      >
                        <option value="1">1 bed property</option>
                        <option value="2">2 bed property</option>
                        <option value="3">3 bed property</option>
                        <option value="4">4 bed property</option>
                        <option value="5">5 bed property</option>
                        <option value="6+">6+ bed property</option>
                      </select>
                    </div>

                    {/* Bathrooms */}
                    <div>
                      <select 
                        value={modalData.totalBathrooms} 
                        onChange={(e) => setModalData({ ...modalData, totalBathrooms: e.target.value })}
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 font-bold appearance-none"
                        style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22currentColor%22 stroke-width=%222%22 stroke-linecap=%22round%22 stroke-linejoin=%22round%22%3E%3Cpolyline points=%226 9 12 15 18 9%22/%3E%3C/svg%3E")', backgroundPosition: 'right 1rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1em' }}
                      >
                        <option value="1">1 bathroom</option>
                        <option value="2">2 bathrooms</option>
                        <option value="3">3 bathrooms</option>
                        <option value="4+">4+ bathrooms</option>
                      </select>
                    </div>

                    {/* Occupants */}
                    <div>
                      <select 
                        value={modalData.totalPeople} 
                        onChange={(e) => setModalData({ ...modalData, totalPeople: e.target.value })}
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 font-bold appearance-none"
                        style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22currentColor%22 stroke-width=%222%22 stroke-linecap=%22round%22 stroke-linejoin=%22round%22%3E%3Cpolyline points=%226 9 12 15 18 9%22/%3E%3C/svg%3E")', backgroundPosition: 'right 1rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1em' }}
                      >
                        <option value="1">1 flatmate</option>
                        <option value="2">2 flatmates</option>
                        <option value="3">3 flatmates</option>
                        <option value="4+">4+ flatmates</option>
                      </select>
                    </div>
                  </div>
                </div>

                <button 
                  onClick={() => {
                    const newTitle = `${modalData.totalBedrooms} Bed ${property.offerType === 'room' ? 'Share House' : 'Property'} in ${modalData.address.split(',')[0] || 'Great Location'}`;
                    handleSaveModal({
                      title: newTitle,
                      address: modalData.address,
                      about_home: JSON.stringify({
                        ...about_home,
                        propertyType: modalData.propertyType,
                        totalBedrooms: modalData.totalBedrooms,
                        totalBathrooms: modalData.totalBathrooms,
                        totalPeople: modalData.totalPeople
                      })
                    });
                  }}
                  disabled={saving}
                  className="w-full bg-[#14b8a6] hover:bg-[#0d9488] text-white py-3.5 rounded-xl font-bold transition-all shadow-md mt-6"
                >
                  {saving ? 'Updating...' : 'Update'}
                </button>
              </div>
            )}

            {/* 2. About Property Modal */}
            {(activeModal === 'aboutProperty' || activeModal === 'whoLivingWith') && (
              <div className="space-y-6">
                <h3 className="text-xl md:text-2xl font-black text-center text-gray-900">
                  {activeModal === 'aboutProperty' ? "What&apos;s great about living in this property?" : "Describe who you&apos;ll be living with"}
                </h3>

                <div>
                  <textarea 
                    value={activeModal === 'aboutProperty' ? modalData.description : modalData.who_living_with}
                    onChange={(e) => setModalData(
                      activeModal === 'aboutProperty' 
                        ? { ...modalData, description: e.target.value }
                        : { ...modalData, who_living_with: e.target.value }
                    )}
                    rows={6}
                    className="w-full p-4 bg-gray-50 border border-gray-200 rounded-2xl text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500 font-bold resize-y"
                    placeholder={activeModal === 'aboutProperty' ? "Describe your property details..." : "Describe the occupants..."}
                  />
                </div>

                <button 
                  onClick={() => handleSaveModal({
                    about_home: JSON.stringify(
                      activeModal === 'aboutProperty'
                        ? { ...about_home, description: modalData.description }
                        : { ...about_home, who_living_with: modalData.who_living_with }
                    )
                  })}
                  disabled={saving}
                  className="w-full bg-[#14b8a6] hover:bg-[#0d9488] text-white py-3.5 rounded-xl font-bold transition-all shadow-md mt-6"
                >
                  {saving ? 'Updating...' : 'Update'}
                </button>
              </div>
            )}

            {/* 3. Accepting Modal */}
            {activeModal === 'accepting' && (
              <div className="space-y-6">
                <h3 className="text-xl md:text-2xl font-black text-center text-gray-900">Accepting</h3>
                
                {/* Active tags grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {ACCEPTING_TAGS.map(tag => {
                    const isSelected = modalData.accepting.includes(tag);
                    return (
                      <button 
                        key={tag}
                        onClick={() => {
                          const updated = isSelected 
                            ? modalData.accepting.filter(t => t !== tag)
                            : [...modalData.accepting, tag];
                          setModalData({ ...modalData, accepting: updated });
                        }}
                        className={`flex items-center justify-between px-3.5 py-3 rounded-xl border text-xs font-bold transition-all ${
                          isSelected 
                            ? 'bg-[#14b8a6]/10 text-[#0d9488] border-[#14b8a6] shadow-sm'
                            : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                        }`}
                      >
                        <span>{tag}</span>
                        {isSelected ? <X size={14} className="text-[#0d9488]" /> : <Plus size={14} />}
                      </button>
                    );
                  })}
                </div>

                <button 
                  onClick={() => handleSaveModal({
                    about_home: JSON.stringify({
                      ...about_home,
                      accepting: modalData.accepting
                    })
                  })}
                  disabled={saving}
                  className="w-full bg-[#14b8a6] hover:bg-[#0d9488] text-white py-3.5 rounded-xl font-bold transition-all shadow-md mt-6"
                >
                  {saving ? 'Updating...' : 'Update'}
                </button>
              </div>
            )}

            {/* 4. Edit Property Features Modal */}
            {activeModal === 'features' && (
              <div className="space-y-6">
                <h3 className="text-xl md:text-2xl font-black text-center text-gray-900">Edit Property Features</h3>
                
                <div className="space-y-5">
                  {/* Parking Dropdown */}
                  <div>
                    <span className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Parking</span>
                    <select 
                      value={modalData.parking}
                      onChange={(e) => setModalData({ ...modalData, parking: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 font-bold appearance-none"
                      style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22currentColor%22 stroke-width=%222%22 stroke-linecap=%22round%22 stroke-linejoin=%22round%22%3E%3Cpolyline points=%226 9 12 15 18 9%22/%3E%3C/svg%3E")', backgroundPosition: 'right 1rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1em' }}
                    >
                      <option value="No parking">No parking</option>
                      <option value="1 parking space">1 parking space</option>
                      <option value="2+ parking spaces">2+ parking spaces</option>
                    </select>
                  </div>

                  {/* Accessibility Features */}
                  <div className="space-y-3">
                    <span className="block text-xs font-bold text-gray-500 uppercase tracking-wider">Home accessibility features</span>
                    
                    <div className="grid grid-cols-2 gap-4">
                      {[
                        { key: 'lift', label: 'Lift' },
                        { key: 'stepFree', label: 'Step-free home' },
                        { key: 'levelAccess', label: 'Level access to home' },
                        { key: 'widerDoorways', label: 'Wider doorways' },
                        { key: 'rollInShower', label: 'Roll-in shower' },
                        { key: 'gripRails', label: 'Bathroom grip rails' }
                      ].map(feature => {
                        const active = modalData.accessibility[feature.key];
                        return (
                          <button
                            key={feature.key}
                            onClick={() => {
                              const updated = {
                                ...modalData.accessibility,
                                [feature.key]: !active
                              };
                              setModalData({ ...modalData, accessibility: updated });
                            }}
                            className={`flex flex-col items-center justify-center p-4 rounded-2xl border text-center transition-all ${
                              active
                                ? 'bg-[#006e80]/10 border-[#006e80] text-[#006e80] shadow-sm'
                                : 'bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100'
                            }`}
                          >
                            <span className="text-xs font-bold">{feature.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="space-y-3 pt-4">
                  <button 
                    onClick={() => handleSaveModal({
                      about_home: JSON.stringify({
                        ...about_home,
                        parking: modalData.parking,
                        accessibility: modalData.accessibility
                      })
                    })}
                    disabled={saving}
                    className="w-full bg-[#006e80] hover:bg-[#005a69] text-white py-3.5 rounded-xl font-bold transition-all shadow-md"
                  >
                    {saving ? 'Saving...' : 'Save and close'}
                  </button>
                  <button 
                    onClick={() => setActiveModal(null)}
                    className="w-full text-center text-xs text-gray-500 hover:text-gray-800 font-bold py-2"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* 5. Room Overview Tabbed Modal */}
            {activeModal === 'roomOverview' && (
              <div className="space-y-6">
                <h3 className="text-xl md:text-2xl font-black text-center text-gray-900 mb-2">
                  {editingRoomIndex !== null ? 'Edit room' : `Add ${modalData.roomName}`}
                </h3>
                
                {/* Internal Tab Headers */}
                <div className="flex gap-4 border-b border-gray-150 overflow-x-auto pb-2 scrollbar-none">
                  {['Rent', 'Details', 'Features', 'Availability', 'Flatmate preference'].map(tab => {
                    const isActive = activeRoomTab === tab;
                    return (
                      <button
                        key={tab}
                        type="button"
                        onClick={() => setActiveRoomTab(tab)}
                        className={`pb-2 text-xs md:text-sm font-bold whitespace-nowrap transition-all relative ${
                          isActive ? 'text-[#006e80]' : 'text-gray-400 hover:text-gray-600'
                        }`}
                      >
                        {tab === 'Features' ? (
                          <span className="flex items-center gap-1">
                            Features 
                            <span className="bg-[#14b8a6] text-white text-[8px] font-black px-1.5 py-0.5 rounded uppercase leading-none">NEW</span>
                          </span>
                        ) : tab}
                        {isActive && (
                          <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-[#006e80] rounded-t-full" />
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Tab content renders based on selection */}
                <div className="min-h-[300px]">
                  
                  {/* Tab 1: Rent */}
                  {activeRoomTab === 'Rent' && (
                    <div className="space-y-5 animate-fadeIn">
                      <div>
                        <span className="block text-sm md:text-base font-extrabold text-gray-800 mb-2">Weekly rent</span>
                        <div className="relative rounded-xl shadow-sm">
                          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-500 font-bold">$</div>
                          <input 
                            type="number"
                            value={modalData.rent}
                            onChange={(e) => setModalData({ ...modalData, rent: e.target.value })}
                            className="w-full pl-9 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#006e80] font-bold"
                          />
                        </div>
                        <p className="text-xs text-gray-400 mt-2 font-bold">$439 is the average weekly rent for a room in your suburb. <span className="text-[#006e80] cursor-pointer hover:underline">Learn more</span></p>
                      </div>

                      {/* Bills Included */}
                      <div>
                        <span className="block text-sm md:text-base font-extrabold text-gray-800 mb-2">Are bills included in rent?</span>
                        <div className="flex flex-wrap gap-2.5">
                          {['Electricity', 'Water', 'Gas', 'Internet', 'Home essentials'].map(bill => {
                            const isSelected = modalData.billsIncludedToggles.includes(bill);
                            return (
                              <button
                                key={bill}
                                type="button"
                                onClick={() => {
                                  const updated = isSelected 
                                    ? modalData.billsIncludedToggles.filter(b => b !== bill)
                                    : [...modalData.billsIncludedToggles, bill];
                                  setModalData({ ...modalData, billsIncludedToggles: updated });
                                }}
                                className={`px-4 py-2 text-xs md:text-sm font-bold rounded-full border transition-all ${
                                  isSelected 
                                    ? 'bg-[#1e2538] text-white border-[#1e2538] shadow-sm'
                                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                                }`}
                              >
                                {bill}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Bond */}
                      <div>
                        <span className="block text-sm md:text-base font-extrabold text-gray-800 mb-2">Bond</span>
                        <div className="relative rounded-xl shadow-sm">
                          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-500 font-bold">$</div>
                          <input 
                            type="number"
                            value={modalData.bond}
                            onChange={(e) => setModalData({ ...modalData, bond: e.target.value })}
                            className="w-full pl-9 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#006e80] font-bold"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Tab 2: Details */}
                  {activeRoomTab === 'Details' && (
                    <div className="space-y-5 animate-fadeIn">
                      {/* Room Name */}
                      <div>
                        <span className="block text-sm md:text-base font-extrabold text-gray-800 mb-2">Room name</span>
                        <input 
                          type="text"
                          value={modalData.roomName}
                          onChange={(e) => setModalData({ ...modalData, roomName: e.target.value })}
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#006e80] font-bold"
                          placeholder="Room 1"
                        />
                      </div>

                      {/* Room Type */}
                      <div>
                        <span className="block text-sm md:text-base font-extrabold text-gray-800 mb-2">Room type</span>
                        <div className="flex border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                          {['Private', 'Shared'].map(type => {
                            const isSelected = modalData.roomType === type;
                            return (
                              <button
                                key={type}
                                type="button"
                                onClick={() => setModalData({ ...modalData, roomType: type })}
                                className={`flex-1 py-3 text-xs md:text-sm font-bold transition-all ${
                                  isSelected 
                                    ? 'bg-[#1e2538] text-white' 
                                    : 'bg-white text-gray-700 hover:bg-gray-50'
                                }`}
                              >
                                {type}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Room Furnishings */}
                      <div>
                        <span className="block text-sm md:text-base font-extrabold text-gray-800 mb-2">Room furnishings</span>
                        <div className="flex border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                          {['Flexible', 'Furnished', 'Unfurnished'].map(opt => {
                            const isSelected = modalData.furnished === opt;
                            return (
                              <button
                                key={opt}
                                type="button"
                                onClick={() => setModalData({ ...modalData, furnished: opt })}
                                className={`flex-1 py-3 text-xs md:text-sm font-bold transition-all ${
                                  isSelected 
                                    ? 'bg-[#1e2538] text-white' 
                                    : 'bg-white text-gray-700 hover:bg-gray-50'
                                }`}
                              >
                                {opt}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Bathroom Access */}
                      <div>
                        <span className="block text-sm md:text-base font-extrabold text-gray-800 mb-2">Bathroom</span>
                        <div className="flex border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                          {['Shared', 'Own', 'Ensuite'].map(opt => {
                            const isSelected = modalData.bathroomAccess === opt;
                            return (
                              <button
                                key={opt}
                                type="button"
                                onClick={() => setModalData({ ...modalData, bathroomAccess: opt })}
                                className={`flex-1 py-3 text-xs md:text-sm font-bold transition-all ${
                                  isSelected 
                                    ? 'bg-[#1e2538] text-white' 
                                    : 'bg-white text-gray-700 hover:bg-gray-50'
                                }`}
                              >
                                {opt}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Tab 3: Features */}
                  {activeRoomTab === 'Features' && (
                    <div className="space-y-5 animate-fadeIn">
                      {/* Bed Size */}
                      <div>
                        <span className="block text-sm md:text-base font-extrabold text-gray-800 mb-2">Bed Size</span>
                        <div className="flex border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                          {['Single', 'Double', 'Queen', 'King', 'None'].map(opt => {
                            const isSelected = modalData.bedSize === opt;
                            return (
                              <button
                                key={opt}
                                type="button"
                                onClick={() => setModalData({ ...modalData, bedSize: opt })}
                                className={`flex-1 py-3 text-[10px] md:text-xs font-bold transition-all ${
                                  isSelected 
                                    ? 'bg-[#1e2538] text-white' 
                                    : 'bg-white text-gray-700 hover:bg-gray-50'
                                }`}
                              >
                                {opt}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Room furnishings and features */}
                      <div>
                        <span className="block text-sm md:text-base font-extrabold text-gray-800 mb-3">Room furnishings and features</span>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {ROOM_FEATURES.map(feat => {
                            const isSelected = modalData.roomFeatures.includes(feat.id);
                            return (
                              <button
                                key={feat.id}
                                type="button"
                                onClick={() => {
                                  const updated = isSelected 
                                    ? modalData.roomFeatures.filter(f => f !== feat.id)
                                    : [...modalData.roomFeatures, feat.id];
                                  setModalData({ ...modalData, roomFeatures: updated });
                                }}
                                className={`flex items-center gap-2 p-3 rounded-xl border text-left text-xs font-extrabold transition-all ${
                                  isSelected 
                                    ? 'bg-[#006e80]/10 border-[#006e80] text-[#006e80] shadow-sm'
                                    : 'bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100'
                                }`}
                              >
                                <span className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${
                                  isSelected ? 'border-[#006e80] bg-[#006e80] text-white' : 'border-gray-300 bg-white'
                                }`}>
                                  {isSelected && <Check size={10} />}
                                </span>
                                <span className="truncate">{feat.label}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Tab 4: Availability */}
                  {activeRoomTab === 'Availability' && (
                    <div className="space-y-5 animate-fadeIn">
                      {/* Date available */}
                      <div>
                        <span className="block text-sm md:text-base font-extrabold text-gray-800 mb-2">Date available</span>
                        <input 
                          type="date"
                          value={modalData.availableDate}
                          onChange={(e) => setModalData({ ...modalData, availableDate: e.target.value })}
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#006e80] font-bold"
                        />
                      </div>

                      {/* Minimum length of stay */}
                      <div>
                        <span className="block text-sm md:text-base font-extrabold text-gray-800 mb-2">Minimum length of stay</span>
                        <select 
                          value={modalData.minStay}
                          onChange={(e) => setModalData({ ...modalData, minStay: e.target.value })}
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#006e80] font-bold appearance-none"
                          style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22currentColor%22 stroke-width=%222%22 stroke-linecap=%22round%22 stroke-linejoin=%22round%22%3E%3Cpolyline points=%226 9 12 15 18 9%22/%3E%3C/svg%3E")', backgroundPosition: 'right 1rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1em' }}
                        >
                          <option value="No minimum stay">No minimum stay</option>
                          <option value="1 month">1 month</option>
                          <option value="2 months">2 months</option>
                          <option value="3 months">3 months</option>
                          <option value="6 months">6 months</option>
                          <option value="12 months">12 months</option>
                        </select>
                      </div>

                      {/* Maximum length of stay */}
                      <div>
                        <span className="block text-sm md:text-base font-extrabold text-gray-800 mb-2">Maximum length of stay</span>
                        <select 
                          value={modalData.maxStay}
                          onChange={(e) => setModalData({ ...modalData, maxStay: e.target.value })}
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#006e80] font-bold appearance-none"
                          style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22currentColor%22 stroke-width=%222%22 stroke-linecap=%22round%22 stroke-linejoin=%22round%22%3E%3Cpolyline points=%226 9 12 15 18 9%22/%3E%3C/svg%3E")', backgroundPosition: 'right 1rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1em' }}
                        >
                          <option value="No maximum stay">No maximum stay</option>
                          <option value="3 months">3 months</option>
                          <option value="6 months">6 months</option>
                          <option value="12 months">12 months</option>
                        </select>
                      </div>
                    </div>
                  )}

                  {/* Tab 5: Flatmate preference */}
                  {activeRoomTab === 'Flatmate preference' && (
                    <div className="space-y-3 animate-fadeIn">
                      <span className="block text-sm md:text-base font-extrabold text-gray-800 mb-3">Who would you prefer to live with?</span>
                      <div className="flex flex-col gap-2">
                        {['Women only', 'Men only', 'No couples', 'Couple', 'Anyone (inc. couples)'].map(pref => {
                          const isSelected = modalData.flatmatePreference === pref;
                          return (
                            <button
                              key={pref}
                              type="button"
                              onClick={() => setModalData({ ...modalData, flatmatePreference: pref })}
                              className={`w-full py-3.5 px-6 rounded-xl border text-sm font-bold text-center transition-all ${
                                isSelected 
                                  ? 'bg-[#1e2538] text-white border-[#1e2538]'
                                  : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                              }`}
                            >
                              {pref}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                </div>

                {/* Modal wizard sequential next & submit buttons */}
                <div className="pt-4 border-t border-gray-100 flex flex-col gap-2.5">
                  {activeRoomTab !== 'Flatmate preference' ? (
                    <button 
                      type="button"
                      onClick={() => handleRoomNextStep(activeRoomTab)}
                      className="w-full bg-[#006e80] hover:bg-[#005a69] text-white py-3.5 rounded-xl font-extrabold transition-all shadow-md text-sm md:text-base flex items-center justify-center gap-2"
                    >
                      Next
                    </button>
                  ) : (
                    <button 
                      type="button"
                      onClick={handleSaveRoom}
                      disabled={saving}
                      className="w-full bg-[#14b8a6] hover:bg-[#0d9488] text-white py-3.5 rounded-xl font-extrabold transition-all shadow-md text-sm md:text-base flex items-center justify-center gap-2"
                    >
                      {saving ? 'Submitting...' : 'Submit'}
                    </button>
                  )}
                  
                  {/* Allow immediate save and close or cancel */}
                  <div className="flex gap-4 w-full">
                    <button 
                      type="button"
                      onClick={handleSaveRoom}
                      disabled={saving}
                      className="flex-1 text-center text-xs text-gray-600 hover:text-gray-900 bg-gray-50 border border-gray-200 hover:bg-gray-100 font-extrabold py-2 rounded-lg"
                    >
                      Save and close
                    </button>
                    <button 
                      type="button"
                      onClick={() => setActiveModal(null)}
                      className="flex-1 text-center text-xs text-gray-500 hover:text-red-500 bg-gray-50 border border-gray-200 hover:bg-gray-100 font-extrabold py-2 rounded-lg"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      )}

    </div></div></div>
  );
}
